import { Box, Button, FormControl, Grid, MenuItem, Select } from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { useAlert } from 'components/UI/alertSakeBar/alertProvider';
import useStandardExam from 'components/hooks/books/useStandardExam';
import { useOragnization } from 'components/hooks/oragnization/useOragnization';
import usePermissionForUser from 'components/hooks/permissions/usePermissionForUser';
import useStateManager from 'components/hooks/useStateManager';
import { groupBy } from 'lodash';
import ContentLoading from 'modules/standardExamReport/contentLoading';
import { downcomming_year, upcomming_year } from 'modules/standardExamReport/helper';
import moment from 'moment-timezone';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  handleGetMostFailedByGrade,
  handleGetMostPassedByGrade,
  handleGetSchoolInfo,
  handleGetStandardExamCount,
  handleGetStudentMissing,
  handleGetTeacherInfo,
  handleListCourseInfo,
  handleListFilterRecords,
} from 'store/standardexams';
import styled from 'styled-components';
import SentryWrap from 'components/Sentry';
import ExamReportSupport from './ExamReportSupport';
import StandardPassedGrade from './StandardGraph/StandardPassedGrade';
import StandardPassesFailed from './StandardGraph/StandardPassesFailed';
import ProficiecnyExamStack from './proficiencyExam/ProficiecnyExamStack';

const OverviewText = styled.span`
  color: black;
  font-size: 22px;
  font-weight: 800;
  display: flex !important;
  align-items: center;
`;

const VerticalDivider = styled.div`
  width: 1px;
  background-color: #e8e8e8;
  height: 100%;
  min-height: 100%;
  margin: 0 8px;
  align-self: stretch;
`;

const flagDataStandardExam = [
  {
    title: 'Red = Urgent Support (high: 50% or more overall students are failing).',
    backgroundcolor: 'linear-gradient(180deg,#ef476f,#d7265e)',
  },
  {
    title: 'Orange = Needs Attention (moderate: 30% or more students overall are failing).',
    backgroundcolor: 'linear-gradient(180deg, #FFB703, #FB8500)',
  },
  {
    title: 'Green = On Track (80% or more students overall are passing).',
    backgroundcolor: 'linear-gradient(180deg, #03989e, #03989e)',
  },
];

const StandardExam = () => {
  const { isDistrictAdmin } = usePermissionForUser();
  const [examReportSupportData, setExamReportSupportData] = useState([]);
  const { allStandardExam } = useStandardExam();
  const currentYearSession = useMemo(() =>
    moment().month() > 7 ? upcomming_year : downcomming_year,
  );

  const sessionOptions = useMemo(() => {
    const unique = Array.from(new Set((allStandardExam || []).map((e) => e.session))).filter(
      Boolean,
    );
    return unique.length ? unique : [currentYearSession];
  }, [allStandardExam, currentYearSession]);

  const [activeSession, setActiveSession] = useState(null);
  const [states, updateStates, updateNestedKey] = useStateManager({
    result: {},
    loading: {
      initial: false,
      studentMissing: false,
      teacher: false,
    },
    filterRecords: {
      selected_standard_exam_passed: null,
      selected_standard_exam_failed: null,
    },
    optionsData: {
      courseInfo: [],
      allTeachers: [],
    },
  });
  const dispatch = useDispatch();
  const { currentOrg } = useOragnization();
  const showAlert = useAlert();
  const abortControllerRef = useRef(null);

  useEffect(() => {
    if (allStandardExam && activeSession == null) {
      const grp = groupBy(allStandardExam, 'session');
      if (grp[upcomming_year]) {
        if (activeSession != upcomming_year) {
          setActiveSession(upcomming_year);
        }
      } else if (grp[downcomming_year]) {
        if (activeSession != downcomming_year) {
          setActiveSession(downcomming_year);
        }
      }
    }
  }, [allStandardExam, activeSession]);

  const fetchInitialData = useCallback(async () => {
    if (!currentOrg?.id || states.loading.initial) return;

    try {
      updateStates((prev) => ({
        ...prev,
        loading: {
          ...prev.loading,
          mostPassedByGrade: true,
          mostFailedByGrade: true,
          initial: true,
        },
      }));

      const payload = {
        org_id: currentOrg.id,
        session: activeSession || currentYearSession,
        courseId: null,
      };

      const [
        standardExamCount,
        courseInfoResult,
        mostPassedByGradeResult,
        mostFailedBySchoolResult,
      ] = await Promise.allSettled([
        // for standard exam count
        dispatch(handleGetStandardExamCount(payload)),

        // for per course
        dispatch(handleListCourseInfo({ organisationId: currentOrg.id })),

        // for most passed by grade
        dispatch(handleGetMostPassedByGrade({ organisationId: currentOrg.id, bookId: null, session: activeSession || currentYearSession })),
        // for most failed by school
        dispatch(handleGetMostFailedByGrade({ organisationId: currentOrg.id, bookId: null, session: activeSession || currentYearSession })),
      ]);
      const standardExamCountResult =
        standardExamCount.status === 'fulfilled' ? standardExamCount.value : null;

      const courseInfo = courseInfoResult.status === 'fulfilled' ? courseInfoResult.value : null;
      const mostPassedByGrade =
        mostPassedByGradeResult.status === 'fulfilled' ? mostPassedByGradeResult.value : null;
      const mostFailedBySchool =
        mostFailedBySchoolResult.status === 'fulfilled' ? mostFailedBySchoolResult.value : null;

      updateStates((prev) => ({
        ...prev,
        result: {
          ...prev.result,
          standardExamCount: standardExamCountResult?.data,
          mostPassedByGrade: mostPassedByGrade?.data,
          mostFailedByGrade: mostFailedBySchool?.data,
        },
        optionsData: {
          ...prev.optionsData,
          courseInfo: courseInfo?.data || [],
        },
        loading: {
          ...prev.loading,
          initial: false,
          mostPassedByGrade: false,
          mostFailedByGrade: false,
        },
      }));
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Error fetching initial data:', error);
        showAlert('Error loading data');
        updateStates((prev) => ({
          ...prev,
          loading: {
            ...prev.loading,
            mostPassedByGrade: false,
            mostFailedByGrade: false,
            initial: false,
          },
        }));
      }
    }
  }, [currentOrg?.id, activeSession]);

  useEffect(() => {
    if (isDistrictAdmin && currentOrg?.id) {
      fetchInitialData();
    }
  }, [isDistrictAdmin, currentOrg?.id]);

  const getStandardExamTakenData = async (bookId = null) => {
    if (!currentOrg?.id || states.loading.initial) return;
    try {
      updateStates({ loading: { ...states.loading, initial: true } });
      let result = null;
      if (bookId) {
        result = await dispatch(
          handleListFilterRecords({ organisationId: currentOrg.id, bookId: bookId }),
        );
      } else {
        result = await dispatch(handleListFilterRecords({ organisationId: currentOrg.id }));
      }
      if (result) {
        updateStates({
          result: {
            ...states.result,
            standardExamTakenData: result.data,
            loading: { ...states.loading, initial: false },
          },
        });
      } else {
        showAlert('Failed to load standard exam taken data');
        updateStates({ loading: { ...states.loading, initial: false } });
      }
    } catch (error) {
      console.error('Error fetching standard exam taken data:', error);
      showAlert('Error loading standard exam taken data');
      updateStates({ loading: { ...states.loading, initial: false } });
    } finally {
      updateStates({ loading: { ...states.loading, initial: false } });
    }
  };

  const getStudentMissing = async (courseId = null) => {
    if (!currentOrg?.id || states.loading.studentMissing) return;

    try {
      updateStates({ loading: { ...states.loading, initial: true } });
      let result = null;
      if (courseId) {
        result = await dispatch(
          handleGetStudentMissing({
            organisationId: currentOrg.id,
            courseId: courseId,
          }),
        );
      } else {
        result = await dispatch(
          handleGetStudentMissing({
            organisationId: currentOrg.id,
          }),
        );
      }

      if (result?.data !== undefined) {
        updateStates({
          result: { ...states.result, student_missing: result.data },
          loading: { ...states.loading, initial: false },
        });
      } else {
        showAlert('Failed to load student missing data');
        updateStates({ loading: { ...states.loading, initial: false } });
      }
    } catch (error) {
      console.error('Error fetching student missing data:', error);
      showAlert('Error loading student data');
      updateStates({ loading: { ...states.loading, initial: false } });
    }
  };

  const getTeacherInfo = async (teacherId = null) => {
    if (!currentOrg?.id || states.loading.teacher) return;
    let result = null;
    try {
      updateStates({ loading: { ...states.loading, initial: true } });
      if (teacherId) {
        result = await dispatch(
          handleGetTeacherInfo({ organisationId: currentOrg.id, teacherId: teacherId }),
        );
      } else {
        result = await dispatch(handleGetTeacherInfo({ organisationId: currentOrg.id }));
      }
      if (result?.data) {
        updateStates({
          result: {
            ...states.result,
            examTakenPerTeacher: result.data?.totalSubmissions,
          },
          loading: { ...states.loading, initial: false },
        });
      }
    } catch (error) {
      console.error('Error fetching teacher info:', error);
      showAlert('Error loading teacher data');
      updateStates({ loading: { ...states.loading, initial: false } });
    }
  };

  const getTeacherExamInfo = async (schoolId = null) => {
    if (!currentOrg?.id || states.loading.teacher) return;
    let result = null;
    try {
      updateStates({ loading: { ...states.loading, initial: true } });
      if (schoolId) {
        result = await dispatch(
          handleGetSchoolInfo({ organisationId: currentOrg.id, schoolId: schoolId }),
        );
      } else {
        result = await dispatch(handleGetSchoolInfo({ organisationId: currentOrg.id }));
      }
      if (result?.data) {
        updateStates({
          result: { ...states.result, examTakenPerSchool: result.data?.totalSubmissions },
          loading: { ...states.loading, initial: false },
        });
      }
    } catch (error) {
      console.error('Error fetching teacher exam info:', error);
      showAlert('Error loading teacher exam data');
      updateStates({ loading: { ...states.loading, initial: false } });
    }
  };

  const getPassedExamByGrade = async (bookIds = null) => {
    if (!currentOrg?.id || states.loading.initial) return;
    try {
      updateStates((prev) => ({ ...prev, loading: { ...prev.loading, mostPassedByGrade: true } }));
      const result = await dispatch(
        handleGetMostPassedByGrade({
          organisationId: currentOrg.id,
          bookId: bookIds,
          session: activeSession || currentYearSession
        }),
      );
      if (result?.data) {
        updateStates((prev) => ({
          ...prev,
          result: { ...prev.result, mostPassedByGrade: result.data },
          loading: { ...prev.loading, mostPassedByGrade: false },
        }));
      }
      return result?.data || [];
    } catch (error) {
      console.error('Error fetching passed exam by grade:', error);
    } finally {
      updateStates((prev) => ({ ...prev, loading: { ...prev.loading, mostPassedByGrade: false } }));
    }
  };

  const getFailedExamByGrade = async (bookIds = null) => {
    if (!currentOrg?.id || states.loading.initial) return;
    try {
      updateStates((prev) => ({ ...prev, loading: { ...prev.loading, mostFailedByGrade: true } }));
      const result = await dispatch(
        handleGetMostFailedByGrade({
          organisationId: currentOrg.id,
          bookId: bookIds,
          session: activeSession || currentYearSession
        }),
      );

      if (result?.data) {
        updateStates((prev) => ({
          ...prev,
          result: { ...prev.result, mostFailedByGrade: result.data },
          loading: { ...prev.loading, mostFailedByGrade: false },
        }));
        return result?.data || [];
      }
      return [];
    } catch (error) {
      console.error('Error fetching failed exam by grade:', error);
    } finally {
      updateStates((prev) => ({ ...prev, loading: { ...prev.loading, mostFailedByGrade: false } }));
    }
  };

  const excelBorderThin = {
    top: { style: 'thin', color: { argb: 'FFB0BEC5' } },
    bottom: { style: 'thin', color: { argb: 'FFB0BEC5' } },
    left: { style: 'thin', color: { argb: 'FFB0BEC5' } },
    right: { style: 'thin', color: { argb: 'FFB0BEC5' } },
  };

  const hexToArgbFont = (hex) => {
    if (!hex || typeof hex !== 'string') return 'FF37474F';
    const s = hex.replace('#', '').trim();
    if (s.length === 6) return `FF${s.toUpperCase()}`;
    return 'FF37474F';
  };

  const downloadStandardExamCountCSV = async () => {
    try {
      const standardExamCount = states?.result?.standardExamCount;

      if (!standardExamCount) {
        return;
      }

      const courseLabel =
        states?.filterRecords?.selected_course && states?.filterRecords?.selected_course !== 'All'
          ? (states?.optionsData?.courseInfo || []).find(
              (item) => item?.id === states?.filterRecords?.selected_course,
            )?.title || 'Selected Course'
          : 'All Courses';

      const ExcelJS = (await import('exceljs')).default;
      const { saveAs } = await import('file-saver');
      const workbook = new ExcelJS.Workbook();
      const sheet = workbook.addWorksheet('Standard Exam Count', {
        views: [{ state: 'frozen', ySplit: 1 }],
      });

      sheet.columns = [
        { width: 22 },
        { width: 14 },
        { width: 36 },
      ];

      const headerRow = sheet.addRow(['Category', 'Count', 'Course']);
      headerRow.height = 22;
      headerRow.eachCell((cell) => {
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FF02796D' },
        };
        cell.font = { bold: true, color: { argb: 'FFFFFFFF' }, size: 12 };
        cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
        cell.border = excelBorderThin;
      });

      const dataRows = [
        ['Exams Taken', standardExamCount?.examTaken ?? 0, courseLabel],
        ['Not Started', standardExamCount?.notStarted ?? 0, courseLabel],
        ['Not Finished', standardExamCount?.notFinished ?? 0, courseLabel],
        ['Not Assigned', standardExamCount?.notAssigned ?? 0, courseLabel],
      ];

      dataRows.forEach((cells, dataIdx) => {
        const row = sheet.addRow(cells);
        row.height = 20;
        const fillArgb = dataIdx % 2 === 0 ? 'FFFFFFFF' : 'FFF5F5F5';
        row.eachCell((cell, colNumber) => {
          cell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: fillArgb },
          };
          cell.font = { size: 11, color: { argb: 'FF263238' } };
          cell.border = excelBorderThin;
          if (colNumber === 2) {
            cell.alignment = { horizontal: 'right', vertical: 'middle' };
          } else {
            cell.alignment = { vertical: 'middle', wrapText: true };
          }
        });
      });

      const buffer = await workbook.xlsx.writeBuffer();
      saveAs(
        new Blob([buffer], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        }),
        `standard_exam_count_${moment().format('DD-MM-YY_hh-mm-ss')}.xlsx`,
      );
    } catch (error) {
      console.error('Error in downloading standard exam count Excel:', error);
    }
  };

  const onChangeCourseStandardExam = async (id) => {
    if (!currentOrg?.id || states.loading.initial) return;
    try {
      updateStates((prev) => ({ ...prev, loading: { ...prev.loading, initial: true } }));
      if (id === 'All' || id === states.filterRecords.selected_course || !id) {
        const data = await dispatch(
          handleGetStandardExamCount({
            org_id: currentOrg.id,
            session: activeSession || currentYearSession,
            taken_course_id: null,
            missing_course_id: null,
          }),
        );
        if (data?.success) {
          updateStates((prev) => ({
            ...prev,
            filterRecords: {
              ...prev.filterRecords,
              selected_course: null,
            },
            result: {
              ...prev.result,
              standardExamCount: data.data,
            },
          }));
        }
        return;
      }
      const data = await dispatch(
        handleGetStandardExamCount({
          org_id: currentOrg.id,
          session: activeSession || currentYearSession,
          taken_course_id: id,
          missing_course_id: null,
        }),
      );
      if (data?.success) {
        updateStates((prev) => ({
          ...prev,
          filterRecords: {
            ...prev.filterRecords,
            selected_course: id,
          },
          result: {
            ...prev.result,
            standardExamCount: data.data,
          },
        }));
      }
      return;
    } catch (error) {
      console.error('Error fetching standard exam count:', error);
      showAlert('Error loading standard exam count');
      updateStates((prev) => ({ ...prev, loading: { ...prev.loading, initial: false } }));
    } finally {
      updateStates((prev) => ({ ...prev, loading: { ...prev.loading, initial: false } }));
    }
  };

  console.log(states?.filterRecords, 123);

  const onChangeCourse = (id = null) => {
    if (id === 'All' || id === states.filterRecords.selected_course || !id) {
      updateNestedKey(['filterRecords', 'selected_course'], null);
      getStudentMissing();
      return;
    }
    updateNestedKey(['filterRecords', 'selected_course'], id);
    getStudentMissing(id);
  };

  const onChangeTeacher = (id = null) => {
    if (id === 'All' || id === states.filterRecords.selected_teacher || !id) {
      updateNestedKey(['filterRecords', 'selected_teacher'], null);
      getTeacherInfo();
      return;
    }
    updateNestedKey(['filterRecords', 'selected_teacher'], id || null);
    getTeacherInfo(id);
  };

  const onChangeSchool = (id = null) => {
    if (id === 'All' || id === states.filterRecords.selected_school || !id) {
      updateNestedKey(['filterRecords', 'selected_school'], null);
      getTeacherExamInfo();
      return;
    }
    updateNestedKey(['filterRecords', 'selected_school'], id || null);
    getTeacherExamInfo(id);
  };

  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  const handleChangePassedByGrade = (item) => {
    if (!item || item === 'All' || item === null) {
      updateNestedKey(['filterRecords', 'selected_standard_exam_passed'], null);
      getPassedExamByGrade();
      return;
    }
    const allBookId = item?.standard_exams?.map((item) => item.bookId);
    if (allBookId?.length === 0 || !allBookId) {
      showAlert('Please Add a book in this grade');
      return;
    }
    updateNestedKey(['filterRecords', 'selected_standard_exam_passed'], item);
    getPassedExamByGrade(allBookId);
  };

  const handleFailedByGrade = (item) => {
    if (!item || item === 'All' || item === null) {
      updateNestedKey(['filterRecords', 'selected_standard_exam_failed'], null);
      getFailedExamByGrade();
      return;
    }
    const allBookId = item?.standard_exams?.map((item) => item.bookId);
    if (allBookId?.length === 0 || !allBookId) {
      showAlert('Dont have a books in this grade');
      return;
    }

    getFailedExamByGrade(allBookId);
    updateNestedKey(['filterRecords', 'selected_standard_exam_failed'], item);
  };

  const downloadExamReportSupportCSV = async () => {
    try {
      if (!examReportSupportData || examReportSupportData.length === 0) {
        showAlert('No data available for download', 'warning');
        return;
      }

      const ExcelJS = (await import('exceljs')).default;
      const { saveAs } = await import('file-saver');
      const workbook = new ExcelJS.Workbook();
      const sheet = workbook.addWorksheet('Teachers Needing Support', {
        views: [{ state: 'frozen', ySplit: 1 }],
      });

      sheet.columns = [
        { width: 28 },
        { width: 26 },
        { width: 20 },
        { width: 20 },
        { width: 16 },
      ];

      const headerRow = sheet.addRow([
        'Teacher Name',
        'School',
        'Result',
        'No. of Assignments',
        'Average Grade',
      ]);
      headerRow.height = 22;
      headerRow.eachCell((cell) => {
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FF02796D' },
        };
        cell.font = { bold: true, color: { argb: 'FFFFFFFF' }, size: 12 };
        cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
        cell.border = excelBorderThin;
      });

      examReportSupportData.forEach((item, dataIdx) => {
        const row = sheet.addRow([
          item.title || '-',
          item.school || item.schoolName || '-',
          item.proprity || '-',
          item.noOfAssignments || item.uniqueAssignments || 0,
          item.averageGrade ?? 0,
        ]);
        row.height = 20;
        const rowFill = dataIdx % 2 === 0 ? 'FFFFFFFF' : 'FFF5F5F5';
        const resultArgb = hexToArgbFont(item.priorityColor);
        row.eachCell((cell, colNumber) => {
          cell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: rowFill },
          };
          cell.font = { size: 11, color: { argb: 'FF263238' } };
          cell.border = excelBorderThin;
          cell.alignment = { vertical: 'middle', wrapText: true };
          if (colNumber === 3) {
            cell.font = { bold: true, size: 11, color: { argb: resultArgb } };
          }
          if (colNumber === 4 || colNumber === 5) {
            cell.alignment = { horizontal: 'right', vertical: 'middle' };
          }
        });
      });

      const buffer = await workbook.xlsx.writeBuffer();
      saveAs(
        new Blob([buffer], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        }),
        `stanadard_exam_teachers_needing_support_${moment().format('DD-MM-YY_hh-mm-ss')}.xlsx`,
      );
    } catch (error) {
      console.error('Error in downloading exam report support Excel:', error);
      showAlert('Error downloading Excel', 'error');
    }
  };

  useEffect(() => {
    document.body.style.overflow = "auto";
    return () => {
      document.body.style.overflow = "auto";
    };
  }, []);
  return (
    <SentryWrap>
    <div>
      <ContentLoading isLoading={states.loading.initial} />
      <div className="flex flex-col gap-20">
        <div className="flex items-center justify-between proficencyHead">
          <OverviewText>
            1WAY&nbsp;
            <span style={{ color: '#fb742b', fontSize: '22px', fontWeight: '900' }}>STANDARDS</span>
          </OverviewText>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 12, fontWeight: 600 }}>Session:</span>
            <select
              value={activeSession}
              onChange={(e) => setActiveSession(e.target.value)}
              style={{
                border: '1px solid #e5e7eb',
                borderRadius: 8,
                padding: '6px 10px',
                fontSize: 12,
                fontWeight: 600,
                background: '#fff',
              }}
            >
              {(sessionOptions || []).map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>
        </div>

        <Box
          sx={{
            width: '100%',
            border: '1px solid #e8e8e8',
            borderRadius: '12px',
            padding: '16px',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              mb: 2,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'flex-end',
                width: '100%',
                marginRight: '10px',
              }}
            >
              <Button
                onClick={downloadStandardExamCountCSV}
                size="small"
                sx={{
                  minWidth: 'auto',
                  width: 28,
                  height: 28,
                  padding: 0,
                  borderRadius: '12px',
                  backgroundColor: '#EDEDED',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  '&:hover': {
                    backgroundColor: '#E0E0E0',
                  },
                  '&:active': {
                    backgroundColor: '#D5D5D5',
                  },
                }}
              >
                <FileDownloadIcon sx={{ fontSize: 18, color: '#61677E' }} />
              </Button>
            </Box>
            <FormControl
              size="small"
              sx={{
                minWidth: 150,
                maxWidth: 250,
                '& .MuiOutlinedInput-root': {
                  height: 32,
                  borderRadius: '8px',
                  fontSize: '12px',
                },
                '& .MuiSelect-select': {
                  py: 0.5,
                  px: 1,
                  fontSize: '12px',
                },
              }}
            >
              <Select
                value={states?.filterRecords?.selected_course || 'All'}
                displayEmpty
                onChange={(e) => {
                  const value = e.target.value;
                  onChangeCourseStandardExam(value);
                }}
                renderValue={(val) => {
                  if (val === 'All' || !val) return 'All';
                  const checkData = (states?.optionsData?.courseInfo || []).find(
                    (item) => item?.id === val,
                  );
                  return checkData?.title || 'All';
                }}
              >
                <MenuItem value="All" sx={{ fontSize: '12px' }}>
                  <em>All</em>
                </MenuItem>
                {(states?.optionsData?.courseInfo || []).map((item) => (
                  <MenuItem key={item?.id} value={item?.id} sx={{ fontSize: '12px' }}>
                    {item?.title}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <div className="flex flex-row gap-20 flex-wrap items-center ">
            <Grid container spacing={2} sx={{ width: '100%', m: 0 }}>
              <Grid item size={{ xs: 12, sm: 6, md: 6, lg: 3 }}>
                <ProficiecnyExamStack
                  value={states?.result?.standardExamCount?.examTaken || 0}
                  label="Standard Exam"
                  subLabel="Exams Taken"
                  backgroundColor="linear-gradient(90deg, #0B0B0B 0%, #2B2A9F 60%, #3533CD 100%)"
                />
              </Grid>
              <Grid item size={{ xs: 12, sm: 6, md: 6, lg: 3 }}>
                <ProficiecnyExamStack
                  value={states?.result?.standardExamCount?.notStarted || 0}
                  label="Standard Exam"
                  subLabel="Not Started"
                  backgroundColor="linear-gradient(90deg, #ff3131, #ff914d)"
                />
              </Grid>
              <Grid item size={{ xs: 12, sm: 6, md: 6, lg: 3 }}>
                <ProficiecnyExamStack
                  value={states?.result?.standardExamCount?.notFinished || 0}
                  label="Standard Exam"
                  subLabel="Not Finished"
                  backgroundColor="linear-gradient(90deg, #8c52ff, #00bf63)"
                />
              </Grid>
              <Grid item size={{ xs: 12, sm: 6, md: 6, lg: 3 }}>
                <ProficiecnyExamStack
                  value={states?.result?.standardExamCount?.notAssigned || 0}
                  label="Standard Exam"
                  subLabel="Not Assigned"
                  backgroundColor="linear-gradient(90deg, #8c52ff, #00bf63)"
                />
              </Grid>
            </Grid>
          </div>
        </Box>

        <Grid container spacing={2} sx={{ width: '100%', m: 0 }}>
          <Grid size={{ xs: 12, md: 12, lg: 5 }} sx={{ border:'1px solid #e8e8e8', borderRadius: '20px'}} item>
            <ExamReportSupport
              from="standard"
              redirectTo="/standard-exam-report"
              flagData={flagDataStandardExam}
              flagEyeModal="standardExamModal"
              activeSession={activeSession}
              onDownload={downloadExamReportSupportCSV}
              onDataLoaded={setExamReportSupportData}
            />
          </Grid>

          <Grid item size={{ xs: 12, md: 12, lg: 7 }} sx={{ width: '100%', m: 0 }}>
            <Box
              sx={{
                width: '100%',
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                gap: 0,
                border: '1px solid #e8e8e8',
                borderRadius: '12px',
                padding: '12px',
                alignItems: 'stretch',
              }}
            >
              <Box sx={{ flex: 1, width: { xs: '100%', md: '50%' } }}>
                <StandardPassedGrade
                  value={states?.filterRecords?.selected_standard_exam_passed}
                  handleChangePassedByGrade={handleChangePassedByGrade}
                  optionsData={allStandardExam}
                  mostPassedByGrade={states?.result?.mostPassedByGrade}
                  loading={states?.loading?.mostPassedByGrade}
                />
              </Box>
              <Box
                sx={{
                  display: { xs: 'none', md: 'flex' },
                  alignItems: 'stretch',
                  justifyContent: 'center',
                }}
              >
                <VerticalDivider />
              </Box>
              <Box sx={{ flex: 1, width: { xs: '100%', md: '50%' } }}>
                <StandardPassesFailed
                  value={states?.filterRecords?.selected_standard_exam_failed}
                  handleFailedByGrade={handleFailedByGrade}
                  optionsData={allStandardExam}
                  mostFailedBySchool={states?.result?.mostFailedByGrade}
                  loading={states?.loading?.mostFailedByGrade}
                />
              </Box>
            </Box>
          </Grid>
        </Grid>
      </div>
    </div>
    </SentryWrap>
  );
};

export default StandardExam;
